import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from "flarum/forum/app";
import Page from "flarum/common/components/Page";
import Button from "flarum/common/components/Button";
import classList from "flarum/common/utils/classList";
import chatState from "../state/chat";
import ChannelFormModal from "./ChannelFormModal";
import { BrowseSkeleton } from "./Skeletons";
import { channelIcon } from "../utils/channelIcon";
import { mobileTitleControl } from "../utils/toolbar";
import { acceptInvitation, adoptChannelPayload, declineInvitation, invitationErrorText } from "../utils/invitations";
/**
 * Discourse's "Browse channels" page: every channel the actor can see, with join
 * controls and — for those who may — a way to create one.
 *
 * This is also the only place a channel can be created, so it is reachable from
 * the sidebar even when the actor has joined nothing.
 */
export default class BrowseChannelsPage extends Page {
  constructor() {
    super(...arguments);
    _defineProperty(this, "channels", []);
    _defineProperty(this, "loading", true);
    _defineProperty(this, "filter", "all");
    _defineProperty(this, "query", "");
    _defineProperty(this, "searchTimer", null);
  }
  oninit(vnode) {
    super.oninit(vnode);
    const title = app.translator.trans("ramon-chat.forum.browse.title", {}, true);
    app.setTitle(title);

    // What gives the phone toolbar its back arrow: core's `Navigation` draws the
    // drawer toggle instead whenever `app.history.canGoBack()` is false, and the
    // stack only grows if a page pushes itself onto it. `TagsPage` does exactly
    // this one line; see the longer note on `ChatPage.recordHistory`.
    app.history.push("chat.browse", title);
    const routeFilter = m.route.param("filter");
    if (routeFilter && ["all", "open", "closed", "archived", "mine"].includes(routeFilter)) {
      this.filter = routeFilter;
    }
    this.load();
  }
  view() {
    return m("div", {
      className: "ChatBrowse"
    }, mobileTitleControl(app.translator.trans("ramon-chat.forum.browse.title", {}, true)), m("button", {
      type: "button",
      className: "ChatBrowse-back",
      onclick: () => m.route.set(app.route("chat.index"))
    }, m("i", {
      className: "fas fa-arrow-left",
      "aria-hidden": "true"
    }), app.translator.trans("ramon-chat.forum.nav.chat")), m("div", {
      className: "ChatBrowse-header"
    }, m("div", {
      className: "ChatBrowse-heading"
    }, m("h1", {
      className: "ChatBrowse-title"
    }, app.translator.trans("ramon-chat.forum.browse.title")), m("p", {
      className: "ChatBrowse-subtitle"
    }, app.translator.trans(app.forum.attribute("canCreateChatChannel") ? "ramon-chat.forum.browse.subtitle" : "ramon-chat.forum.browse.subtitle_join_only"))), app.forum.attribute("canCreateChatChannel") ? m(Button, {
      className: "Button Button--primary",
      icon: "fas fa-plus",
      onclick: () => this.create()
    }, app.translator.trans("ramon-chat.forum.browse.new_channel")) : null), m("div", {
      className: "ChatBrowse-toolbar"
    }, m("div", {
      className: "ChatBrowse-filters",
      role: "group",
      "aria-label": app.translator.trans("ramon-chat.forum.browse.title", {}, true)
    }, ["all", "open", "closed", "archived", "mine"].map(f => m("button", {
      key: f,
      type: "button",
      className: classList("ChatBrowse-filter", {
        "ChatBrowse-filter--active": this.filter === f
      }),
      "aria-pressed": this.filter === f,
      onclick: () => this.setFilter(f)
    }, app.translator.trans("ramon-chat.forum.browse.filter_".concat(f))))), m("div", {
      className: "ChatBrowse-search"
    }, m("i", {
      className: "ChatBrowse-search-icon fas fa-magnifying-glass",
      "aria-hidden": "true"
    }), m("input", {
      className: "ChatBrowse-search-input",
      type: "search",
      placeholder: app.translator.trans("ramon-chat.forum.browse.search_placeholder", {}, true),
      value: this.query,
      oninput: e => this.onSearch(e.target.value)
    }), this.query !== "" ? m("button", {
      type: "button",
      className: "ChatBrowse-search-clear",
      title: app.translator.trans("ramon-chat.forum.browse.clear_search", {}, true),
      onclick: () => this.clearSearch()
    }, m("i", {
      className: "fas fa-times",
      "aria-hidden": "true"
    })) : null)), this.loading ? BrowseSkeleton() : this.channels.length === 0 ? this.empty() : m("div", {
      className: "ChatBrowse-list"
    }, this.channels.map(channel => this.card(channel))));
  }

  /**
   * Nothing to show.
   *
   * Two different situations wearing one sentence before: a forum with no
   * channels at all, and a search that matched none. The first needs a way to
   * create one, the second needs a way back to the full list — and being told
   * "no channels match" on a forum that has none reads as a bug.
   */
  empty() {
    const searching = this.query.trim() !== "";
    return m("div", {
      className: "ChatBrowse-empty"
    }, m("div", {
      className: "ChatBrowse-empty-icon",
      "aria-hidden": "true"
    }, m("i", {
      className: searching ? "fas fa-magnifying-glass" : "fas fa-comments"
    })), m("p", {
      className: "ChatBrowse-empty-text"
    }, searching ? app.translator.trans("ramon-chat.forum.browse.empty_search", {
      query: this.query.trim()
    }) : app.translator.trans("ramon-chat.forum.browse.empty")), searching ? m(Button, {
      className: "Button",
      onclick: () => this.clearSearch()
    }, app.translator.trans("ramon-chat.forum.browse.clear_search")) : app.forum.attribute("canCreateChatChannel") ? m(Button, {
      className: "Button Button--primary",
      icon: "fas fa-plus",
      onclick: () => this.create()
    }, app.translator.trans("ramon-chat.forum.browse.new_channel")) : null);
  }
  card(channel) {
    var _channel$userCount, _channel$messagesCoun;
    return m("div", {
      className: classList("ChatBrowseCard", {
        "ChatBrowseCard--frozen": !channel.isOpen()
      }),
      key: channel.id()
    }, m("div", {
      className: "ChatBrowseCard-head"
    }, channelIcon(channel, "ChatBrowseCard-icon"), m("div", {
      className: "ChatBrowseCard-headText"
    }, m("div", {
      className: "ChatBrowseCard-name"
    }, m("span", null, channel.displayName())), m("div", {
      className: "ChatBrowseCard-badges"
    }, channel.isPrivate() ? m("span", {
      className: "ChatBrowseCard-status"
    }, m("i", {
      className: "ChatBrowseCard-lock fas fa-lock",
      "aria-hidden": "true"
    }), app.translator.trans("ramon-chat.forum.new_channel.private")) : null, channel.isOpen() ? null : m("span", {
      className: "ChatBrowseCard-status ChatBrowseCard-status--frozen"
    }, m("i", {
      className: channel.isArchived() ? "fas fa-box-archive" : "fas fa-lock-open",
      "aria-hidden": "true"
    }), app.translator.trans(channel.isArchived() ? "ramon-chat.forum.browse.filter_archived" : "ramon-chat.forum.browse.filter_closed")), channel.threadingEnabled() ? m("span", {
      className: "ChatBrowseCard-status"
    }, m("i", {
      className: "fas fa-code-branch",
      "aria-hidden": "true"
    }), app.translator.trans("ramon-chat.forum.channel.threading_enabled")) : null, channel.isFollowing() && channel.isHiddenMember() ? m("span", {
      className: "ChatBrowseCard-status ChatBrowseCard-status--joined"
    }, m("i", {
      className: "fas fa-user-secret",
      "aria-hidden": "true"
    }), app.translator.trans("ramon-chat.forum.browse.inspecting")) : channel.isFollowing() ? m("span", {
      className: "ChatBrowseCard-status ChatBrowseCard-status--joined"
    }, m("i", {
      className: "fas fa-check",
      "aria-hidden": "true"
    }), app.translator.trans("ramon-chat.forum.browse.joined")) : channel.isInvited() ? m("span", {
      className: "ChatBrowseCard-status ChatBrowseCard-status--invited"
    }, m("i", {
      className: "fas fa-envelope-open",
      "aria-hidden": "true"
    }), app.translator.trans("ramon-chat.forum.browse.invited")) : null))), m("div", {
      className: "ChatBrowseCard-description"
    }, channel.description() || app.translator.trans("ramon-chat.forum.browse.no_description")), m("div", {
      className: "ChatBrowseCard-footer"
    }, m("div", {
      className: "ChatBrowseCard-meta"
    }, m("span", null, m("i", {
      className: "fas fa-user",
      "aria-hidden": "true"
    }), app.translator.trans("ramon-chat.forum.channel.members", {
      count: (_channel$userCount = channel.userCount()) != null ? _channel$userCount : 0
    })), m("span", null, m("i", {
      className: "fas fa-comment",
      "aria-hidden": "true"
    }), app.translator.trans("ramon-chat.forum.browse.messages", {
      count: (_channel$messagesCoun = channel.messagesCount()) != null ? _channel$messagesCoun : 0
    }))), m("div", {
      className: "ChatBrowseCard-action"
    }, channel.canEdit() && channel.isCategory() ? m("button", {
      type: "button",
      className: "ChatBrowseCard-iconButton",
      title: app.translator.trans("ramon-chat.forum.channel.edit", {}, true),
      onclick: () => this.edit(channel)
    }, m("i", {
      className: "fas fa-pen-to-square",
      "aria-hidden": "true"
    })) : null, channel.isFollowing() ? m(Button, {
      className: "Button",
      icon: "fas fa-arrow-right",
      onclick: () => this.open(channel)
    }, app.translator.trans("ramon-chat.forum.browse.open")) : channel.isInvited() ? m('[', null, m("button", {
      type: "button",
      className: "ChatBrowseCard-iconButton",
      title: app.translator.trans("ramon-chat.forum.channel.decline_invite", {}, true),
      onclick: () => this.answerInvitation(channel, false)
    }, m("i", {
      className: "fas fa-xmark",
      "aria-hidden": "true"
    })), m(Button, {
      className: "Button Button--primary",
      icon: "fas fa-check",
      onclick: () => this.answerInvitation(channel, true)
    }, app.translator.trans("ramon-chat.forum.channel.accept_invite"))) : m('[', null, channel.canJoinHidden() ? m("button", {
      type: "button",
      className: "ChatBrowseCard-iconButton",
      title: app.translator.trans("ramon-chat.forum.channel.join_hidden", {}, true),
      onclick: () => this.join(channel, true)
    }, m("i", {
      className: "fas fa-user-secret",
      "aria-hidden": "true"
    })) : null, channel.canJoin() ? m(Button, {
      className: "Button Button--primary",
      onclick: () => this.join(channel)
    }, app.translator.trans("ramon-chat.forum.channel.join")) : null))));
  }

  // ── Behaviour ──────────────────────────────────────────────────────────────

  setFilter(filter) {
    this.filter = filter;
    this.load();
  }

  /** Debounced so typing does not issue a request per keystroke. */
  onSearch(value) {
    this.query = value;
    if (this.searchTimer !== null) window.clearTimeout(this.searchTimer);
    this.searchTimer = window.setTimeout(() => this.load(), 350);
  }

  /** Back to the unfiltered list, without waiting out the search debounce. */
  clearSearch() {
    if (this.searchTimer !== null) {
      window.clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }
    this.query = "";
    this.load();
  }
  async load() {
    this.loading = true;
    m.redraw();
    const filter = {};

    // 'all' deliberately sends no status filter: closed and archived channels are
    // browsable, they are simply frozen.
    if (this.filter === "open" || this.filter === "closed" || this.filter === "archived") {
      filter.status = this.filter;
    }
    if (this.filter === "mine") {
      filter.following = true;
    }
    if (this.query.trim() !== "") {
      filter.q = this.query.trim();
    }
    try {
      const results = await app.store.find("chat-channels", {
        filter,
        sort: "-lastMessageAt",
        page: {
          limit: 50
        }
      });

      // Direct channels are not browsable — they are private by construction.
      this.channels = (Array.isArray(results) ? results : []).filter(c => c.isCategory());
    } catch (e) {
      this.channels = [];
      // Not `browse.empty`: a request that failed is not a forum without
      // channels, and saying so sends the reader off to create one that already
      // exists.
      app.alerts.show({
        type: "error"
      }, app.translator.trans("ramon-chat.forum.browse.load_failed"));
    } finally {
      this.loading = false;
      m.redraw();
    }
  }
  create() {
    app.modal.show(ChannelFormModal, {
      onSaved: channel => this.open(channel)
    });
  }
  edit(channel) {
    // Reload after saving: a renamed or re-categorised channel may no longer
    // match the active filter or search.
    app.modal.show(ChannelFormModal, {
      channel,
      onSaved: () => this.load()
    });
  }

  /**
   * Joins, or with `hidden` inspects: in the channel with no place in the
   * member list, nothing announced and nothing notified. The server refuses
   * the hidden form for anyone without `inspectChannels`.
   */
  async join(channel, hidden) {
    if (hidden === void 0) {
      hidden = false;
    }
    try {
      const payload = await app.request({
        method: "POST",
        url: "".concat(app.forum.attribute("apiUrl"), "/chat-channels/").concat(channel.id(), "/join"),
        body: {
          data: {
            attributes: {
              hidden
            }
          }
        }
      });

      // The server's record, capability flags included, so opening the channel
      // straight from here draws the composer instead of a "closed" notice.
      if (!adoptChannelPayload(payload)) {
        var _channel$userCount2;
        channel.pushAttributes({
          isFollowing: true,
          isHiddenMember: hidden,
          canPostMessage: channel.isOpen(),
          userCount: hidden ? channel.userCount() : ((_channel$userCount2 = channel.userCount()) != null ? _channel$userCount2 : 0) + 1
        });
      }
      chatState.rememberChannel(channel);
      if (hidden) {
        app.alerts.show({
          type: "success"
        }, app.translator.trans("ramon-chat.forum.channel.joined_hidden"));
      }
      m.redraw();
    } catch (e) {
      app.alerts.show({
        type: "error"
      }, invitationErrorText(e, "ramon-chat.forum.channel.join_failed"));
    }
  }

  /**
   * Accepts or declines an invitation from the card. Accepting leaves the
   * card in place as a joined one; declining leaves it as a plain channel the
   * reader may still join on their own.
   */
  async answerInvitation(channel, accept) {
    if (!accept && !confirm(app.translator.trans("ramon-chat.forum.channel.decline_invite_confirm", {}, true))) {
      return;
    }
    try {
      if (accept) {
        await acceptInvitation(Number(channel.id()));
      } else {
        await declineInvitation(Number(channel.id()));
      }
      app.alerts.show({
        type: "success"
      }, app.translator.trans(accept ? "ramon-chat.forum.channel.invite_accepted" : "ramon-chat.forum.channel.invite_declined"));
      m.redraw();
    } catch (e) {
      app.alerts.show({
        type: "error"
      }, invitationErrorText(e, "ramon-chat.forum.channel.invite_failed"));
    }
  }
  open(channel) {
    chatState.setActiveChannel(Number(channel.id()));
    m.route.set(app.route("chat.channel", {
      id: channel.id()
    }));
  }
}
flarum.reg.add('ramon-chat', 'forum/components/BrowseChannelsPage', BrowseChannelsPage);